import axios from 'axios'

import {API_BASE, apiErrorHandler, ApiFilter, isDefinedAndNotNull, isNotEmpty} from '../api'
import {objectToSnakeCase} from '../utils'
import Auth from '../auth'

const REQUEST_ENDPOINT = `${API_BASE}requests/`
const ADMIN_REQUEST_ENDPOINT = `${API_BASE}admin/requests/`

export default class RequestAPI {
  constructor(cls) {
    this.cls = cls
  }

  static create(cls) {
    return new RequestAPI(cls)
  }

  create(request) {
    const data = this.cls.toAPI(request)
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    return axios
      .post(REQUEST_ENDPOINT, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({apiName: 'Start Request API error'}))
  }

  list({pagination, filters}) {
    const filtersMap = {
      // Pagination
      page: ApiFilter.create({key: 'page'}),
      pageSize: ApiFilter.create({key: 'page_size'}),

      // Sorting
      ordering: ApiFilter.create({key: 'ordering'}),

      // Filtering
      statuses: ApiFilter.create({
        key: 'status',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      organizations: ApiFilter.create({
        key: 'organization',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      subOrganizations: ApiFilter.create({
        key: 'sub_organization',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      referredOnly: ApiFilter.create({key: 'referred_only'}),
      nonReferredOnly: ApiFilter.create({key: 'non_referred_only'}),
      submittedStatuses: ApiFilter.create({
        key: 'submitted_status',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      reportableEventCategories: ApiFilter.create({
        key: 'reportable_event_categories',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      eventCategories: ApiFilter.create({
        key: 'event_categories',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),

      // employee
      employee: ApiFilter.create({key: 'employee'}),

      // Staff member assigned
      assignedTo: ApiFilter.create({key: 'assigned_to'}),
    }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
      params: ApiFilter.buildParams(filtersMap, {...pagination, ...filters}),
    }

    const promise = axios
      .get(`${REQUEST_ENDPOINT}`, options)
      .then(response => response.data)
      .then(data => {
        return {
          ...data,
          results: data.results.map(this.cls.fromAPI),
        }
      })
      .catch(
        apiErrorHandler({
          apiName: 'List Requests API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  retrieve(requestId) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }

    const promise = axios
      .get(`${REQUEST_ENDPOINT}${requestId}/`, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({apiName: 'Retrieve Request API error'}))
    return promise
  }

  async update(request = null, fields = [], excludeFields = [], kwargs = null) {
    // NIP 3/11/20 Temporarily disabling - this IS a request! (or it was at one point...)
    // this.cls.checkIsInstance(request, 'The `request` argument')
    const url = `${REQUEST_ENDPOINT}${(request && request.id) || kwargs.id}/`
    let data = this.cls.toAPI(request, fields, excludeFields, kwargs)
    const options = {
      headers: Auth.getHeaders(),
    }
    return axios
      .patch(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({apiName: 'Update Request API error', rethrowErrors: true}))
  }

  /**
   * Keep alive ping to set editing timestamp of request
   */
  editingPing(request) {
    const url = `${REQUEST_ENDPOINT}${request.id}/editing/`
    const options = {
      headers: Auth.getHeaders(),
    }
    return axios.post(url, {}, options)
  }

  /**
   * Update status of a request
   */
  async updateStatus(request) {
    const url = `${REQUEST_ENDPOINT}${request.id}/update-status/`

    // Create the data structure expected by the API
    const data = {
      status: request.status,
    }
    if (request.status === this.cls.options.DECLINED) {
      data.declined_reason = request.declinedReason
      data.declined_reason_detail = request.declinedReasonDetail
    }
    if (
      request.status === this.cls.options.APPROVED ||
      request.status === this.cls.options.PARTIALLY_APPROVED ||
      request.status === this.cls.options.PARTIALLY_APPROVED_PENDING
    ) {
      data.amount = request.amount
    }

    if (request.payoutType) {
      data.payout_type = request.payoutType
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Update Status Request API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  /**
   * Refer a request to an admin at an organization
   */
  async refer(request) {
    const url = `${REQUEST_ENDPOINT}${request.id}/refer-to-organization/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, {}, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Refer Request API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  /**
   * Logs an admin viewing request details
   */
  async logAdminView(request) {
    const url = `${REQUEST_ENDPOINT}${request.id}/log-view/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, {}, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Log Request View API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  async adminPartialUpdate(request, fields) {
    const url = `${ADMIN_REQUEST_ENDPOINT}${request.id}/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }

    const data = this.cls.toAPI(request, fields)
    const promise = axios
      .patch(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Admin Partial Update API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }
}
